import { isUgcProduct, sortByProductRank, type UgcPhoto, type UgcProduct } from './ugc';

// Filters and search for /customer-projects: two rows of single-choice buttons (product and setting),
// a third "type" row once a product is chosen (its system, species or finish), and a search box. Deliberately shallower than viewrail.com/gallery's filter rail — this gallery is
// for browsing real installs by system, not for drilling into mounts and finishes. Labels follow the
// main gallery's so the vocabulary matches. Options no photo carries are hidden.

export interface FilterOption {
  id: string;
  label: string;
  /** Context key and value this option matches, e.g. product = glass-railing. */
  field: string;
  value: string;
}

export interface FilterGroup {
  id: string;
  label: string;
  options: FilterOption[];
}

const option = (field: string, value: string, label: string): FilterOption => ({ id: value, label, field, value });

export const FILTER_GROUPS: FilterGroup[] = [
  {
    id: 'product',
    label: 'Product',
    options: [
      option('product', 'floating-stairs', 'FLIGHT Stairs'),
      option('product', 'spiral-stairs', 'Spiral Stairs'),
      option('product', 'cable-railing', 'Cable Railing'),
      option('product', 'rod-railing', 'Rod Railing'),
      option('product', 'glass-railing', 'Glass Railing'),
      option('product', 'stair-treads', 'Stair Treads'),
      option('product', 'handrail', 'Handrail'),
    ],
  },
  {
    id: 'location',
    label: 'Setting',
    options: [option('location', 'interior', 'Interior'), option('location', 'exterior', 'Exterior')],
  },
];

/**
 * The finer "type" options offered once a product is chosen. Context tags describe the whole photo,
 * not each product in it — a floating stair with glass railing carries both `mono` (the stair) and
 * `vedera` (the glass) — so each product lists only the tags that describe *it*: stair systems and
 * wood species for stairs, glass systems for glass, metal finishes for cable and rod. Options no
 * photo of that product carries are hidden.
 */
const WOOD_SPECIES = [
  option('finish', 'white-oak', 'White Oak'),
  option('finish', 'hard-maple', 'Hard Maple'),
  option('finish', 'ash', 'Ash'),
  option('finish', 'tumbleweed', 'Tumbleweed'),
  option('finish', 'custom-stain', 'Custom Stain'),
];
const METAL_FINISHES = [
  option('finish', 'jet-black', 'Jet Black'),
  option('finish', 'onyx', 'Onyx'),
  option('finish', 'brushed-stainless', 'Brushed Stainless'),
  option('finish', 'polished-silver', 'Polished Silver'),
  option('finish', 'white', 'White'),
];
export const TYPE_OPTIONS: Partial<Record<UgcProduct, FilterOption[]>> = {
  'floating-stairs': [
    option('subsystem', 'mono', 'FLIGHT Mono'),
    option('subsystem', 'stack', 'FLIGHT Stack'),
    option('subsystem', 'cantilever', 'FLIGHT Cantilever'),
    ...WOOD_SPECIES,
  ],
  'glass-railing': [
    option('subsystem', 'vedera', 'Vedera'),
    option('subsystem', 'talon-spigots', 'Talon Spigots'),
    option('subsystem', 'base-rail', 'Base Rail'),
    option('subsystem', 'hidden-side-mount', 'Hidden Side Mount'),
    option('subsystem', 'standoff-pins', 'Standoff Pins'),
    option('subsystem', 'posts', 'Posts'),
    option('subsystem', 'ascend-talons', 'Ascend Talons'),
  ],
  'cable-railing': [
    option('cable_system', 'signature', 'Signature Cable'),
    option('cable_system', 'express', 'Express Cable'),
    option('cable_system', 'drivetite', 'DriveTite'),
    ...METAL_FINISHES,
  ],
  'rod-railing': METAL_FINISHES,
  'stair-treads': WOOD_SPECIES,
  handrail: [option('subsystem', 'top-cap', 'Top Cap'), ...WOOD_SPECIES],
};

/** The chosen option id per group id (plus `type`). Missing = the group's "All". */
export type FilterSelection = Record<string, string>;

const photoHas = (photo: UgcPhoto, opt: FilterOption) => photo.facets[opt.field]?.includes(opt.value) ?? false;

/** Groups with only the options some photo carries; a group left with one option or none is dropped. */
export const availableGroups = (photos: UgcPhoto[]): FilterGroup[] =>
  FILTER_GROUPS.map((group) => ({ ...group, options: group.options.filter((opt) => photos.some((p) => photoHas(p, opt))) })).filter(
    (group) => group.options.length > 1,
  );

// Search runs over the visible text a visitor would recognise: caption, alt text and the labels of
// every filter and type option the photo matches ("glass" or "vedera" find photos whose captions
// don't say it),
// plus the project number. Every word must match the start of some word in that text, so "glas hall"
// finds glass hallways and "4442" finds project 444265 — until a digit breaks the sequence ("4443").
const optionLabels = (photo: UgcPhoto, options: FilterOption[]) => options.filter((opt) => photoHas(photo, opt)).map((opt) => opt.label);

const searchText = (photo: UgcPhoto) =>
  [
    photo.caption,
    photo.alt,
    // Each project number as written and with its punctuation removed, so "AB-123" is found by
    // "AB-123", "ab 123" or "ab123".
    ...photo.projects.flatMap((project) => [project, project.replace(/[^a-z0-9]/gi, '')]),
    ...FILTER_GROUPS.flatMap((group) => optionLabels(photo, group.options)),
    // System, species and finish names ("Vedera", "White Oak"), per product the photo shows.
    ...photo.products.flatMap((product) => optionLabels(photo, TYPE_OPTIONS[product] ?? [])),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const tokenize = (query: string) => query.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

// The photo code a customer quotes (VR-7K2M4) finds that one photo, written with or without its
// punctuation. It matches whole rather than by prefix: indexed as words, every coded photo would
// carry "vr" plus a random fragment, and short searches like "vr" or "44" would hit unrelated photos.
const matchesPhotoCode = (photo: UgcPhoto, tokens: string[]) =>
  !!photo.code && tokens.join('') === photo.code.replace(/[^a-z0-9]/gi, '').toLowerCase();

const matchesSearch = (photo: UgcPhoto, tokens: string[]) => {
  if (!tokens.length) return true;
  if (matchesPhotoCode(photo, tokens)) return true;
  const words = tokenize(searchText(photo));
  return tokens.every((token) => words.some((word) => word.startsWith(token)));
};

const selectedProduct = (selection: FilterSelection) => (isUgcProduct(selection.product) ? selection.product : undefined);

/** Type options for the selected product that at least one photo in `photos` carries. */
export const availableTypes = (photos: UgcPhoto[], selection: FilterSelection): FilterOption[] => {
  const product = selectedProduct(selection);
  if (!product) return [];
  const ofProduct = photos.filter((p) => p.products.includes(product));
  return (TYPE_OPTIONS[product] ?? []).filter((opt) => ofProduct.some((p) => photoHas(p, opt)));
};

export const filterPhotos = (photos: UgcPhoto[], groups: FilterGroup[], selection: FilterSelection, query: string) => {
  const tokens = tokenize(query);
  const product = selectedProduct(selection);
  const type = product ? TYPE_OPTIONS[product]?.find((opt) => opt.id === selection.type) : undefined;
  const matching = photos.filter(
    (photo) =>
      matchesSearch(photo, tokens) &&
      (!type || photoHas(photo, type)) &&
      groups.every((group) => {
        const chosen = group.options.find((opt) => opt.id === selection[group.id]);
        return !chosen || photoHas(photo, chosen);
      }),
  );
  // With a product chosen, photos mainly of that product lead (see productRank).
  return product ? sortByProductRank(matching, product) : matching;
};

// URL state: ?product=glass-railing&location=interior&q=hallway — the same `product` param the
// gallery sections link with. Unknown values are ignored.
export const selectionFromParams = (params: Record<string, string | string[] | undefined>): { selection: FilterSelection; query: string } => {
  const selection: FilterSelection = {};
  for (const group of FILTER_GROUPS) {
    const value = params[group.id];
    if (typeof value === 'string' && group.options.some((opt) => opt.id === value)) selection[group.id] = value;
  }
  // A type only means something under its product.
  const product = selectedProduct(selection);
  if (product && typeof params.type === 'string' && TYPE_OPTIONS[product]?.some((opt) => opt.id === params.type)) selection.type = params.type;
  const query = typeof params.q === 'string' ? params.q.slice(0, 100) : '';
  return { selection, query };
};

export const selectionToSearch = (selection: FilterSelection, query: string) => {
  const params = new URLSearchParams();
  for (const group of FILTER_GROUPS) if (selection[group.id]) params.set(group.id, selection[group.id]);
  if (selection.type && selection.product) params.set('type', selection.type);
  if (query.trim()) params.set('q', query.trim());
  return params.toString();
};

/** Display name for a product ("Cable Railing"), as on its filter button. */
export const ugcProductLabel = (product: UgcProduct) => FILTER_GROUPS[0].options.find((opt) => opt.id === product)?.label;

/** /customer-projects query pre-filtered to one product, for the gallery sections' "See more". */
export const productFilterSearch = (product: UgcProduct, type?: string) => selectionToSearch({ product, ...(type ? { type } : {}) }, '');

/** Page-N link for the current filters (page 1 omits the param). */
export const pageSearch = (selection: FilterSelection, query: string, page: number) => {
  const params = new URLSearchParams(selectionToSearch(selection, query));
  if (page > 1) params.set('page', String(page));
  return params.toString();
};

/**
 * Which URL a /customer-projects view should name as canonical, and whether it should be indexed.
 * The page and its product views (?product=cable-railing), each paginated, are the indexable set —
 * one page per product, competing for "<product> photos". Finer views (type, setting, search) point
 * at their product's first page so search engines don't index thousands of near-duplicates, and
 * search results are also kept out of the index.
 */
export const canonicalFor = (selection: FilterSelection, query: string, page: number) => {
  const product = selection.product;
  const refined = Boolean(selection.type || selection.location || query.trim());
  return {
    search: pageSearch(product ? { product } : {}, '', refined ? 1 : page),
    index: !query.trim(),
  };
};
